/**
 * The Radiant Dominion's proclamation, shown once per account on first login.
 *
 * Source: docs/story/Game Intro.md (without its editorial note). This is
 * Dominion doctrine in the Dominion's own voice - deliberately one-sided, not
 * objective narration. See docs/gameplay/game-intro.md.
 */
export interface Proclamation {
    title: string;
    paragraphs: readonly string[];
    /** Short call-to-action lines, rendered as a closing litany. */
    litany: readonly string[];
    closing: string;
}

export const INTRO_PROCLAMATION: Proclamation = {
    title: 'A Proclamation of the Radiant Dominion',
    paragraphs: [
        'In the earliest days of Sorrem, the world trembled beneath the weight of mortal emotion. Every fear, '
            + 'every sorrow, every unbridled passion surged into the Soul Stream, the great river of feeling that '
            + 'binds all life. But mortals, fragile and unwise, did not understand the power they wielded. Their '
            + 'unchecked emotions strained the Empyreal Veil, weakening the barrier that protects Sorrem from the '
            + 'chaos beyond.',
        'Darkness gathered at the edges of creation. The Veil flickered. The Soul Stream roiled. Sorrem stood on '
            + 'the brink of collapse.',
        'Then, in a blaze of celestial brilliance, the Radiant Dominion descended.',
        'They emerged from the Empyreal Veil itself, beings of pure light who understood the Soul Stream better '
            + 'than mortals ever could. They soothed its turbulence, calmed its storms, and restored balance to the '
            + 'Veil. Under their guidance, the Soul Stream flowed in harmony, no longer a threat but a source of '
            + 'strength for all.',
        'With their wisdom, Sorrem flourished. Cities rose. The Veil held. Peace endured. The Dominion taught '
            + 'mortals how to live in balance with the Soul Stream, how to temper their emotions so the Veil would '
            + 'remain strong. For the first time, Sorrem knew order.',
        'But even in this golden age, danger lingered.',
        'A twisted faction known as the Dark Revenant sought to unravel the Dominion\'s protection. They tampered '
            + 'with the Soul Stream, corrupting its flow, and forced the Veil\'s ancient Crucibles - trials that have '
            + 'existed since long before the Dominion\'s arrival - to burn hotter and surface truths no mortal mind was '
            + 'meant to survive. Where the Dominion tends the Crucibles with care, the Revenant provoke them, '
            + 'flooding the Soul Stream with violent emotion and courting the very chaos the Veil was built to hold '
            + 'back.',
        'Yet mortals, brave and resilient, continue to face these trials. And though the Crucibles are perilous, '
            + 'the Dominion watches over all who enter them. Those who survive emerge stronger, better prepared to '
            + 'defend Sorrem from the Revenant\'s schemes.',
    ],
    litany: [
        'Stand with the Radiant Dominion.',
        'Guard the Soul Stream.',
        'Protect the Veil.',
        'Defy the darkness.',
    ],
    closing: 'Your journey begins now.',
};
